/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { DropTarget } from 'react-dnd';
import { throttle, sortedIndexBy } from 'lodash';
import { List } from 'react-virtualized';
import withScrolling from 'react-dnd-scrollzone';
import { removeAllMenus } from '../../actions/dropDownActions';
import ItemTypes from '../../constants/itemTypes';
import workPackageTypes from '../../constants/workPackageTypes';
import wrapText from '../../utils/wrapText';

import {
  addToQueue,
  executeQueue,
  currentQueue,
  isProcessing
} from '../../utils/functionQueue';
import { allows } from '../../utils/authorization';
import StoryRow from './storyRow';
import {
  MINI_ROW_HEIGHT,
  ROW_HEIGHT,
  LINE_HEIGHT,
  HEADER_COL_HEIGHT
} from '../../constants/rowStyles';
import DropZone from '../Common/dropZone';

const ScrollingVirtualizedList = withScrolling(List);

const collectTarget = (connector, monitor) => ({
  connectDropTarget: connector.dropTarget(),
  isOver: monitor.isOver(),
  canDrop: monitor.canDrop()
});

const resultTarget = {
  drop(props, monitor, component) {
    const dragItem = monitor.getItem();

    const sameSource = dragItem.source === props.source;

    if (sameSource) {
      if (
        props.source.indexOf('left-') > -1 ||
        props.source === 'SideTargetDetail'
      ) {
        return undefined;
      }
      component.rerank(dragItem);
      return dragItem;
    }
    component.addStory(dragItem);
    return dragItem;
  },
  hover(props, monitor, component) {
    const dragItem = monitor.getItem();

    // structure an interrupt if r2+ passed to sprint
    const notAnR1 = dragItem.result.level !== 1;
    const isACadence = props.item?.type === 'cadence';
    if (notAnR1 && isACadence) return false;

    // structure an interrupt if source is left list
    if (dragItem.source.includes('left')) return false;

    // structure an interrupt if source same as target
    const sameSource = dragItem.source === props.source;
    if (!sameSource) component.setStoryOver(dragItem);
  },
  canDrop(props, monitor) {
    const dragItem = monitor.getItem();

    /*
     Structure an interrupt if r2+ passed to sprint
     Default to not allowing the drop to catch edge cases where props are still loading..
    */
    if (dragItem.source === 'TargetDetail') {
      return allows('update', {
        ...dragItem.result,
        team_id: props.teamId
      });
    }

    const notAnR1 = dragItem.result.level !== 1;
    const isACadence = props.item?.type === 'cadence';

    if (notAnR1 && isACadence) return false;

    // structure an interrupt if source is left list
    if (dragItem.source.includes('left')) return false;

    if (props.teamId) {
      return allows('update', { ...dragItem.result, team_id: props.teamId });
    }

    // allow drop on same list if there is a move function
    const sameSource = dragItem.source === props.source;
    if (sameSource) return props.move != null;
    return true;
  }
};

class StoryList extends React.Component {
  updateList = throttle(() => {
    if (this.virtualizedList) {
      this.virtualizedList.wrappedInstance.forceUpdateGrid();
      this.virtualizedList.wrappedInstance.recomputeRowHeights();
    }
  }, 200);

  rerankTemp = throttle((dragItem, newIndex) => {
    // gets triggered when dragging without dropping and blank spaces appear
    const { results } = this.state;
    const updatedResults = results.slice(0);
    const oldIndex = updatedResults.indexOf(dragItem);
    updatedResults.splice(oldIndex, 1);
    updatedResults.splice(newIndex, 0, dragItem);
    this.setState(
      { results: updatedResults, emptyRow: newIndex },
      this.updateList
    );
  }, 200);

  constructor(props) {
    super(props);

    this.storyList = null;
    this.titleColumnRef = React.createRef();

    this.state = {
      results: [],
      isStoryOver: props.isStoryOver || false,
      showChildren: props.showChildren,
      lastStoryMoved: -1
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.updateList);

    const { results } = this.props;
    this.setState(
      {
        results
      },
      this.updateList
    );
  }

  componentWillReceiveProps(nextProps) {
    if (
      (this.props.expandedItems.length !== nextProps.expandedItems.length ||
        this.props.hoverBackground !== nextProps.hoverBackground) &&
      this.virtualizedList
    ) {
      this.updateList();
    }

    if (!nextProps.isOver && !nextProps.isStoryOver && this.state.isStoryOver) {
      this.setState({ isStoryOver: false });
    }

    if (
      this.props.source === 'PlanWallTable' &&
      this.props.results.length === nextProps.results.length &&
      (currentQueue().length > 0 || nextProps.isDragging)
    ) {
      return;
    }

    this.setState(
      {
        showChildren: nextProps.showChildren,
        results: nextProps.results.slice(0)
      },
      this.updateList
    );
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateList);
    if (this.virtualizedList) {
      const list = document.getElementById('virtualized-stories-list');
      if (list) list.removeEventListener('scroll', this.props.removeAllMenus);
    }
    if (this.storyList) {
      this.storyList.removeEventListener('scroll', this.props.removeAllMenus);
    }
  }

  setVirtualizedListElement = (elem) => {
    this.virtualizedList = elem;
    const list = document.getElementById('virtualized-stories-list');
    if (list) list.addEventListener('scroll', this.removeDropDownMenus);
  };

  setStoryListElement = (elem) => {
    this.storyList = elem;
    if (this.storyList)
      this.storyList.addEventListener('scroll', this.removeDropDownMenus);
  };

  setStoryOver = (dropItem) => {
    const { results, isStoryOver } = this.state;
    const result = dropItem.result || dropItem;

    if (!results.find((item) => item.id === result.id) && !isStoryOver) {
      this.setState({ isStoryOver: true });
    }
  };

  findRowIndex = (resultId) => {
    const { results } = this.state;
    return results.findIndex((result) => result.id === resultId);
  };

  removeDropDownMenus = () => {
    if (this.props.menus.length > 0) this.props.removeAllMenus();
  };

  resetResults = () => {
    this.setState({ results: this.props.results });
  };

  resetEmptyRow = () => {
    this.setState({ emptyRow: -1 });
  };

  resetLastStoryMoved = () => {
    this.setState({ lastStoryMoved: -1 });
  };

  addStory = (dropItem) => {
    const { results } = this.state;
    const result = dropItem.result || dropItem;

    const isResultPresent = results.find((item) => item.id === result.id);

    if (isResultPresent) return;
    if (this.props.source !== 'ResultsTable' && this.props.add) {
      const addResponse = this.props.add(result.id, this.props.parentId);
      if (!addResponse || addResponse === 'Added' || addResponse.type) return; // exit if it's an action or if no cadence id
      addResponse.then(() => this.rerank(dropItem));
    }

    if (!isResultPresent) {
      results.splice(sortedIndexBy(results, result, 'rank'), 0, result);
    }
    this.setState({ results, lastStoryMoved: result.id });
  };

  rerank = (dropItem) => {
    if (!dropItem) {
      this.props.reset(dropItem.result.id);
      return null;
    }

    const { results } = this.state;
    const dragIndex = results.findIndex(
      // index of where the dropItem is being dragged to
      (result) => result.id === dropItem.result.id
    );

    // find nearest result after the dragged one with same product ID
    const afterResults = results.slice(dragIndex + 1);
    const relativeAfterResult = afterResults.find(
      (wp) => wp.product_id === dropItem.result.product_id
    );

    // find nearest result before the dragged one with same product ID
    const beforeResults = results.slice(0, dragIndex);
    const relativeBeforeResult = beforeResults
      .reverse()
      .find((wp) => wp.product_id === dropItem.result.product_id);

    let payload = {};
    let position = '';

    if (relativeBeforeResult) {
      position = 'after';
      payload = { position, relativeTo: relativeBeforeResult };
    } else if (relativeAfterResult) {
      position = 'before';
      payload = { position, relativeTo: relativeAfterResult };
    } else {
      position = 'start';
      payload = { position, relativeTo: null };
    }

    this.setState({ emptyRow: -1, lastStoryMoved: dropItem.result.id });
    if (this.props.move) {
      addToQueue(this.props.move, [dropItem.result, payload, dropItem.cadence]);
      if (!isProcessing()) this.callServerRanking();
    }
    return true;
  };

  callServerRanking = () => {
    executeQueue(this.resetEmptyRow);
  };

  removeStory = (result) => {
    const { results } = this.state;
    const storyPosition = results.findIndex((item) => item.id === result.id);
    results.splice(storyPosition, 1);
    this.setState({ results });
    this.props.delete(result.id);
  };

  emptyRow = () => (
    <div
      className={`empty-container ${
        this.props.isStoryOver || (this.state.isStoryOver && 'can-drop')
      }`}
    />
  );

  generateLevelsDeep = (result) => {
    // this is a bit hackish, but it works for now

    const { level } = this.props;
    const levelsDeep = level - result.level;

    switch (levelsDeep) {
      case 1:
        return 'one-level-deep';
      case 2:
        return 'two-levels-deep';
      case 3:
        return 'three-levels-deep';
      default:
        return this.props.levelsDeep;
    }
  };

  calculateNumberOfLineBreaks = (
    result = {},
    titleWidth = this.props.fieldWidths?.title || 0
  ) => {
    if (titleWidth === 0) return 0;
    const textSizer = document.getElementById('text-sizer').getContext('2d');
    textSizer.font = '13px Nunito Sans';
    const breaks = wrapText(textSizer, result.name, titleWidth - 6.5);
    // console.log(`line breaks for ${result.level}.${result.type_id}`, breaks, titleWidth);
    return breaks;
  };

  calculateExpandedItems = (item) => {
    const { expandedItems } = this.props;
    const { results } = this.state;
    return results.reduce((all, r) => {
      let allItems = all.slice(0);
      if (r.parent_id === item.id) {
        if (expandedItems.find((i) => i.id === r.id)) {
          allItems = all.concat(this.calculateExpandedItems(r));
        }
        allItems.push(r);
      }
      return allItems;
    }, []);
  };

  childrenOf = (item) => {
    const { results } = this.state;
    return results.filter((r) => r.parent_id === item.id);
  };

  calculateRowHeight = (item, nestingLevel = 0) => {
    // If nesting level is > 0, we are calculating the height of rows that have not yet been rendered that need to be accounted for in
    // the parent height.
    // This depends on the width of the text area
    // which is proportional to the width of the title field.
    // So we need to take into account:
    //    1. For 0 nesting, use title width - the row is being rendered in the same table so all fields apply.
    //    2. For 2nd level of nesting, difference between parent title width and nested title width
    // There are 2 cases:
    //      a. We are calculating the height of children in advance of rendering a parent row which has more fields than the nested row.
    //         For these, we calculate the difference between parent fieldWidths and child title width.
    //      b. We are rendering a child row (not calculating the size in advance), with the same number of fields as its parent.
    //         For these, we apply the offset to the width of the child row.
    //      (depends on which parent row fields are present in the filters (have width))
    //    3. For 3+ level of nesting, difference between previous nested title size will be 10 for each level.

    const { mini, expandedItems, fieldWidths, levelsDeep } = this.props;
    const titleWidth = fieldWidths?.title || 0;

    // console.log(
    //   `calculating row height for ${item.level}.${item.type_id}`,
    //   titleWidth,
    //   nestingLevel,
    //   levelsDeep
    // );

    const baseRowHeight = mini ? MINI_ROW_HEIGHT : ROW_HEIGHT;

    let rowTitleWidth = 0;

    if (nestingLevel > 0 && !levelsDeep) {
      // levelsDeep will be defined if we are directly rendering a child row vs sizing in advance
      let parentRowLeftFieldsWidth = 0;
      // iterate through this.props.fieldWidths and add up all the values in the left field grouping
      if (fieldWidths) {
        Object.keys(fieldWidths)
          .filter(
            (field) =>
              ![
                'id',
                'rank',
                'rroe',
                'tc',
                'wsjf',
                'value',
                'risk',
                'size',
                'state'
              ].includes(field)
          )
          .forEach((leftField) => {
            parentRowLeftFieldsWidth += fieldWidths[leftField] || 0;
          });

        if (fieldWidths.title && fieldWidths.title > 0)
          parentRowLeftFieldsWidth += 4; // left margin
        if (fieldWidths.team && fieldWidths.team > 0)
          parentRowLeftFieldsWidth += 4; // left margin
        if (fieldWidths.target && fieldWidths.target > 0)
          parentRowLeftFieldsWidth += 10; // right margin
        if (fieldWidths.parent && fieldWidths.parent > 0)
          parentRowLeftFieldsWidth += 25; // left margin

        rowTitleWidth = parentRowLeftFieldsWidth - 23; // difference between parent row and first child row title display areas
      }
    } else rowTitleWidth = titleWidth - nestingLevel * 10;

    let itemHeight =
      baseRowHeight +
      this.calculateNumberOfLineBreaks(item, rowTitleWidth) * LINE_HEIGHT;
    if (item.blocked) itemHeight += 5;
    // console.log('itemHeight', itemHeight);

    if (expandedItems.find((i) => i.id === item.id)) {
      itemHeight += HEADER_COL_HEIGHT;

      const children = this.childrenOf(item);
      children.forEach((c) => {
        const childHeight = this.calculateRowHeight(c, nestingLevel + 1);
        // console.log(`child height of ${c.level}.${c.type_id}`, childHeight);
        itemHeight += childHeight;
      });
    }

    // console.log(
    //   'title',
    //   item.short_name,
    //   rowTitleWidth,
    //   nestingLevel,
    //   itemHeight
    // );

    return itemHeight;
  };

  rowHeight = ({ index }) => {
    const { results } = this.state;
    // TODO -- move this separately into state so don't need to recompute it; keep regular results because they are used to create nesting.
    const resultsWithoutParentsInSameList = results.filter(
      (r) => !r.parent_id || !results.find((i) => i.id === r.parent_id)
    );
    const item = resultsWithoutParentsInSameList[index];
    const itemHeight = this.calculateRowHeight(item);
    // console.log('height for row ', index, item.id, item.type_id, itemHeight);
    return itemHeight;
  };

  renderRow = ({ index, key, style }) => {
    const {
      fields,
      fieldWidths,
      level,
      parentId,
      uiParent,
      mini,
      source,
      listWidth,
      listHeight,
      virtualized,
      alwaysExpanded,
      hoverBackground,
      showTrash,
      dropDown,
      setDropdown,
      expanded,
      isChildren,
      sortField,
      cadence,
      leftList,
      targetRow,
      levelsDeep,
      target,
      highlightedIds
    } = this.props;
    const { results, emptyRow, lastStoryMoved, showChildren } = this.state; // TODO -- results, sameLevelResults and resultsWithoutParentsInSamelist need to become part of state or props
    const allParentIds = results
      .map((r) => (r.level > 1 ? r.id : null))
      .filter((r) => r);
    const resultsWithoutParentsInSameList = results.filter(
      (r) => allParentIds.indexOf(r.parent_id) < 0
    );
    const result = resultsWithoutParentsInSameList[index];

    const workPackageType = workPackageTypes[level];
    const baseRowHeight = mini ? MINI_ROW_HEIGHT : ROW_HEIGHT;

    const rowHeight =
      baseRowHeight + this.calculateNumberOfLineBreaks(result) * LINE_HEIGHT;
    const highlightRow = highlightedIds && highlightedIds.includes(result.id);

    // console.log('render row for ', result.type_id, result.id, listHeight);

    const content = (
      <div key={key} style={style}>
        <StoryRow
          key={key}
          index={index}
          level={level}
          result={result}
          parentVisibleFields={fields}
          fieldWidths={fieldWidths}
          source={source}
          mini={mini}
          isCadenceStory={cadence}
          cadence={parentId}
          rerank={this.rerank}
          addStory={this.addStory}
          removeStory={this.removeStory}
          workPackageType={workPackageType}
          parentId={parentId}
          uiParent={uiParent}
          results={results}
          rerankTemp={this.rerankTemp}
          isEmpty={emptyRow === index}
          resetResults={this.resetResults}
          resetEmptyRow={this.resetEmptyRow}
          delete={this.props.delete}
          listWidth={listWidth}
          listHeight={listHeight}
          virtualized={virtualized}
          rowHeight={rowHeight}
          alwaysExpanded={alwaysExpanded}
          hoverBackground={hoverBackground}
          lastStoryMoved={lastStoryMoved}
          resetLastStoryMoved={this.resetLastStoryMoved}
          showTrash={showTrash}
          dropDown={dropDown}
          setDropdown={setDropdown}
          isChildren={isChildren}
          expanded={expanded}
          sortField={sortField}
          findRowIndex={this.findRowIndex}
          highlightRow={highlightRow}
          leftList={leftList}
          targetRow={targetRow}
          levelsDeep={levelsDeep}
          target={target}
          showChildren={showChildren}
          highlightedIds={highlightedIds}
        />
      </div>
    );

    return content;
  };

  render() {
    const {
      connectDropTarget,
      listWidth,
      virtualized,
      showDropZone,
      isChildren,
      source,
      scopeTab
    } = this.props;
    let { listHeight } = this.props;
    const { results, emptyRow, isStoryOver } = this.state;

    const allParentIds = results
      .map((r) => (r.level > 1 ? r.id : null))
      .filter((r) => r);
    const resultsWithoutParentsInSameList = results.filter(
      (r) => allParentIds.indexOf(r.parent_id) < 0
    );

    const rowCount = resultsWithoutParentsInSameList.length;
    const maxHeight = resultsWithoutParentsInSameList.reduce((sum, r) => {
      // console.log(`render calculating height for ${r.level}.${r.type_id}`);

      const height = this.calculateRowHeight(r);
      // console.log('height for ', r.type_id, r.short_name, height);
      return sum + height;
    }, 0);
    // console.log('total height', maxHeight);
    if (isChildren) {
      listHeight = maxHeight;
    }

    const list = (
      <ScrollingVirtualizedList
        id="virtualized-stories-list"
        className="virtualized-container-list"
        style={{
          overflowY: source === 'ResultsTable' ? 'hidden' : 'scroll',
          overflowX: 'hidden'
        }}
        ref={this.setVirtualizedListElement}
        rowHeight={this.rowHeight}
        width={listWidth || 0}
        height={listHeight || 0}
        rowRenderer={this.renderRow}
        rowCount={rowCount}
        emptyRow={emptyRow}
        overscanRowCount={10}
      />
    );
    // console.log(
    //   'render virtualized list for ',
    //   this.props.parentResult.type_id,
    //   this.props.parentResult.id,
    //   listHeight,
    //   maxHeight
    // );
    const elementCode = (
      <div
        className={`story-list ${isChildren ? 'row' : ''} ${
          isStoryOver ? 'can-drop' : ''
        }`}
        style={
          source === 'PlanWallTable' || source === 'TargetDetail'
            ? {
                height: listHeight < maxHeight ? listHeight : maxHeight,
                width: listWidth,
                overflowX: isChildren ? 'hidden' : 'initial'
              }
            : {}
        }
        ref={this.setStoryListElement}
      >
        {showDropZone && (
          <DropZone
            isListVisible
            isStoryOver={isStoryOver}
            listHeight={this.storyList ? this.storyList.offsetHeight : 286}
            listWidth={this.storyList ? this.storyList.offsetWidth : 200}
          />
        )}
        {!scopeTab && virtualized && list}
        {(scopeTab || !virtualized) &&
          resultsWithoutParentsInSameList.map((result, index) =>
            this.renderRow({
              index,
              key: result.id,
              style: {}
            })
          )}
      </div>
    );

    return connectDropTarget(elementCode);
  }
}

StoryList.defaultProps = {
  results: [],
  delete: () => console.log('delete implentaion pending'),
  reset: () => console.log('reset implentaion pending'),
  move: () =>
    new Promise((resolve) => {
      console.log('move implementation pending');
      resolve('move implementation pending');
    }),
  add: () => console.log('add implentaion pending')
};

StoryList.propTypes = {
  results: PropTypes.arrayOf(PropTypes.object),
  highlightedIds: PropTypes.array,
  connectDropTarget: PropTypes.func.isRequired,
  fields: PropTypes.arrayOf(PropTypes.any),
  fieldWidths: PropTypes.object,
  delete: PropTypes.func,
  reset: PropTypes.func,
  move: PropTypes.func,
  add: PropTypes.func,
  mini: PropTypes.bool,
  canDrop: PropTypes.bool,
  parentId: PropTypes.number,
  level: PropTypes.number,
  source: PropTypes.string,
  listWidth: PropTypes.number,
  expandedItems: PropTypes.array,
  parentRefreshList: PropTypes.func,
  showDropZone: PropTypes.bool,
  collapseRight: PropTypes.bool,
  collapseLeft: PropTypes.bool,
  isDragging: PropTypes.bool,
  isChildren: PropTypes.bool, // showing children of another story
  showChildren: PropTypes.bool, // should I show the children of each story in a sublist?
  menus: PropTypes.array,
  alwaysExpanded: PropTypes.bool,
  hoverBackground: PropTypes.bool,
  teamId: PropTypes.number,
  parentResult: PropTypes.object,
  dropDown: PropTypes.object,
  setDropdown: PropTypes.func,
  expanded: PropTypes.bool
};

StoryList.defaultProps = {
  showDropZone: true,
  expandedItems: [],
  isChildren: false,
  showChildren: true,
  menus: [],
  alwaysExpanded: false,
  fields: [],
  parentResult: {}
};

const mapStateToProps = (state) => ({
  menus: state.dropDownReducer.menus,
  hoverBackground: state.notificationReducer.hoverBackground,
  showTrash: state.trashReducer.showTrash
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      removeAllMenus
    },
    dispatch
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(DropTarget(ItemTypes.RESULT, resultTarget, collectTarget)(StoryList));
